import { Injectable } from '@angular/core';
import { environment } from '../../enviroment/enviroment';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TokenStorageService } from './token-storage.service';


export interface UserTokenData {
  sub?: string;
  'cognito:username'?: string;
  'cognito:groups'?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  email_verified?: boolean;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly API_BASE_URL = environment.apiUrl;


  // URLs principales para autenticación
  private readonly authPaths = {
    register: '/auth/register',      // POST: Registrar usuario
    confirm: '/auth/confirm-signup', // POST: Confirmar registro
    signin: '/auth/signin',          // POST: Iniciar sesión ⭐
    resendCode: '/auth/resend-confirmation',
    user: '/auth/user',              // GET: Obtener usuario actual (requiere auth) ⭐
    signout: '/auth/signout',        // POST: Cerrar sesión (requiere auth)
    refresh: '/auth/refresh'         // POST: Renovar token (requiere auth)
  };

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
    private readonly tokenStorage: TokenStorageService
  ) { }


  /**
   * Registra un nuevo usuario con grupo (nuevo flujo)
   */

  async registerUser(
    email: string,
    password: string,
    given_name: string,
    family_name: string,
    document_number: string,
    group: string,
    alojamientos_ids?: string
  ): Promise<{ message: string, userId: string, requiresConfirmation: boolean, addedGroup?: string, allGroups?: string[] }> {
    const body: any = {
      email,
      password,
      given_name,
      family_name,
      document_number,
      group
    };

    if (alojamientos_ids) {
      body.alojamientos_ids = alojamientos_ids;
    }

    let headersConfig: any = {
      'Content-Type': 'application/json'
    };
    const accessToken = this.tokenStorage.getAccessToken();

    if (accessToken) {
      headersConfig['Authorization'] = `Bearer ${accessToken}`;
    }

    const headers = new HttpHeaders(headersConfig);

    try {
       return await firstValueFrom(
        this.http.post<{
          message: string,
          userId: string,
          requiresConfirmation: boolean,
          addedGroup?: string,
          allGroups?: string[]
        }>(`${this.API_BASE_URL}${this.authPaths.register}`, body, { headers })
      );

    } catch (error) {
      console.error('Error completo en registro:', error);
      throw this.handleError(error);
    }
  }

  // El método registerUserWithRole ya no es necesario con grupos, pero puedes dejarlo para compatibilidad si lo usas en otros flujos.

  /**
   * Confirma el registro del usuario con el código recibido
   */
  async confirmSignUp(email: string, code: string): Promise<{ message: string }> {
    try {
      return await firstValueFrom(
        this.http.post<{ message: string }>(
          `${this.API_BASE_URL}${this.authPaths.confirm}`,
          { email, code }
        )
      );
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Inicia sesión y almacena los tokens, ahora maneja grupos
   */
  async signIn(email: string, password: string): Promise<any> {
    try {
      const response = await firstValueFrom(
        this.http.post<any>(`${this.API_BASE_URL}${this.authPaths.signin}`, { email, password })
      );
      // Si el backend responde con error NEW_PASSWORD_REQUIRED, lanzar objeto especial con session
      if (response && response.error === 'NEW_PASSWORD_REQUIRED') {
        // Guardar la session en localStorage para el flujo de cambio de clave
        if (response.session) {
          localStorage.setItem('cognitoSession', response.session);
        }
        throw new Error('NEW_PASSWORD_REQUIRED');
      }
      // Almacenar tokens
      this.tokenStorage.setTokens(response);
      // Extraer usuario y grupos directamente del idToken
      const idToken = response.idToken;
      let user: any = {};
      if (idToken) {
        try {
          const payload = JSON.parse(atob(idToken.split('.')[1]));
          user = {
            id: payload.sub || payload['cognito:username'],
            email: payload.email,
            emailVerified: payload.email_verified,
            givenName: payload.given_name,
            familyName: payload.family_name,
            groups: payload['cognito:groups'] || (response.user?.groups ?? []),
            primaryGroup: response.user?.primaryGroup ?? ((payload['cognito:groups'] && payload['cognito:groups'][0]) || 'user'),
            attributes: payload
          };
        } catch (e) {
          console.error('Error parsing idToken payload:', e);
        }
      }
      this.tokenStorage.setUser(user);
      return response;
    } catch (error: any) {
      if (
        error?.error?.error === 'NEW_PASSWORD_REQUIRED' ||
        error?.error === 'NEW_PASSWORD_REQUIRED'
      ) {
        // Si el backend responde con error y session, guardar session
        if (error?.error?.session) {
          localStorage.setItem('cognitoSession', error.error.session);
        }
        throw new Error('NEW_PASSWORD_REQUIRED');
      }
      throw this.handleError(error);
    }
  }

  /**
   * Reenvía el código de confirmación
   */
  async resendConfirmationCode(email: string): Promise<{ message: string }> {
    try {
      return await firstValueFrom(
        this.http.post<{ message: string }>(
          `${this.API_BASE_URL}${this.authPaths.resendCode}`,
          { email }
        )
      );
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Obtiene la información del usuario autenticado
   */
  async getUser(): Promise<any> {
    const accessToken = this.tokenStorage.getAccessToken();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    return await firstValueFrom(
      this.http.get(`${this.API_BASE_URL}${this.authPaths.user}`, {
        headers: new HttpHeaders({
          'Authorization': `Bearer ${accessToken}`
        })
      })
    );
  }

  /**
   * Verifica si el usuario está autenticado y renueva token si es necesario
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const token = this.tokenStorage.getAccessToken();
      if (!token) {
        return false;
      }

      // Verificar si el token ha expirado
      if (this.tokenStorage.isAccessTokenExpired()) {
        console.log('🔄 Token expirado, intentando renovar...');
        try {
          await this.refreshToken();
          return true;
        } catch (error) {
          console.error('❌ Error renovando token:', error);
          return false;
        }
      }

      // Verificar si debe renovarse próximamente (5 minutos antes de expirar)
      if (this.tokenStorage.shouldRefreshToken()) {
        console.log('🔄 Token próximo a expirar, renovando preventivamente...');
        try {
          await this.refreshToken();
        } catch (error) {
          console.warn('⚠️ Error en renovación preventiva:', error);
          // No fallar si la renovación preventiva falla
        }
      }

      return true;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  }

  /**
   * Cierra la sesión del usuario
   */
  async signOut(): Promise<void> {
    try {
      const accessToken = this.tokenStorage.getAccessToken();

      if (accessToken) {
        await firstValueFrom(
          this.http.post(`${this.API_BASE_URL}${this.authPaths.signout}`, {}, {
            headers: new HttpHeaders({
              'Authorization': `Bearer ${accessToken}`
            })
          })
        );
      }
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      // Limpiar tokens localmente siempre
      this.tokenStorage.clearTokens();
      this.router.navigate(['/']);
    }
  }

  /**
   * Renueva el token de acceso usando el refresh token
   */
  async refreshToken(): Promise<void> {
    const accessToken = this.tokenStorage.getAccessToken();
    if (!accessToken) {
      console.error('[DEBUG][refreshToken] No access token available');
      throw new Error('No access token available');
    }

    const refreshToken = this.tokenStorage.getRefreshToken();
    if (!refreshToken) {
      console.error('[DEBUG][refreshToken] No refresh token available');
      throw new Error('No refresh token available');
    }

    // Obtener el cognitoUsername desde el token almacenado
    const user = this.getUserFromToken();
    const cognitoUsername = user?.sub || user?.['cognito:username'];
    if (!cognitoUsername) {
      console.error('[DEBUG][refreshToken] No cognitoUsername available. user:', user);
      throw new Error('No cognitoUsername available');
    }

    const body = { refreshToken, cognitoUsername };
    console.log('[DEBUG][refreshToken] Body enviado:', body);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });

    try {
      const response = await firstValueFrom(
        this.http.post<{
          accessToken: string,
          idToken: string,
          expiresIn: number,
          tokenType: string
        }>(
          `${this.API_BASE_URL}${this.authPaths.refresh}`,
          body,
          { headers }
        )
      );
      console.log('[DEBUG][refreshToken] Respuesta recibida:', response);

      // Actualizar tokens
      this.tokenStorage.updateTokens(response);
    } catch (error) {
      console.error('[DEBUG][refreshToken] Error:', error);
      // Si falla el refresh, cerrar sesión
      this.signOut();
      throw this.handleError(error);
    }
  }

  /**
   * Obtiene datos básicos del usuario desde el token (fallback)
   */
  getUserFromToken(): UserTokenData | null {
    try {
      const idToken = this.tokenStorage.getIdToken();
      if (!idToken) return null;

      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return payload;
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }

  /**
   * Obtiene solo el ID del usuario desde el token
   */
  getUserIdFromToken(): string | null {
    try {
      const tokenData = this.getUserFromToken();
      if (!tokenData) return null;

      return tokenData.sub || tokenData['cognito:username'] || null;
    } catch (error) {
      console.error('Error getting user ID from token:', error);
      return null;
    }
  }

  /**
   * Verifica si hay un token válido disponible
   */
  hasValidToken(): boolean {
    try {
      const token = this.tokenStorage.getAccessToken();
      if (!token) return false;

      return !this.tokenStorage.isAccessTokenExpired();
    } catch (error) {
      console.error('Error checking token validity:', error);
      return false;
    }
  }


  /**
   * Actualiza el atributo custom:alojamientos_ids del usuario autenticado en Cognito
   */
  async addAlojamientoIdToUser(alojamientos_ids: any , userId:any): Promise<any> {
    console.log("DATA")
  console.log(alojamientos_ids,userId)

    const body = {
      alojamientos_ids
    };
    const accessToken = this.tokenStorage.getAccessToken();
    if (!accessToken) {
      throw new Error('No hay token de acceso disponible');
    }
  // TypeScript
const headers = new HttpHeaders({
  'Content-Type': 'application/json',
  'x-user-id': String(userId),
});

    console.log('[DEBUG][addAlojamientoIdToUser] Body enviado:', body);
    try {
      const result = await firstValueFrom(
        this.http.post<any>(this.API_BASE_URL+'/hostings/list', body, { headers })
      );

      console.log('[DEBUG][addAlojamientoIdToUser] Respuesta completa:', result);
      return result;
    } catch (result) {
      console.error('[DEBUG][addAlojamientoIdToUser] Error:', result);
      throw result;
    }
  }


  /**
 * Elimina o agrega un alojamiento al atributo custom:alojamientos_ids usando action
 */
  async updateAlojamientoIdOfUser(alojamientos_ids: string, action: 'add' | 'remove' | 'replace' = 'add', email?: string): Promise<any> {
    let targetEmail = email;
    if (!targetEmail) {
      const user = this.getUserFromToken();
      if (!user || !user.email) {
        throw new Error('No se pudo obtener el email del usuario autenticado');
      }
      targetEmail = user.email;
    }
    const body = {
      email: targetEmail,
      alojamientos_ids,
      action
    };
    const accessToken = this.tokenStorage.getAccessToken();
    if (!accessToken) {
      throw new Error('No hay token de acceso disponible');
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    });
    try {
      const result = await firstValueFrom(
        this.http.put<any>(`${this.API_BASE_URL}/auth/user`, body, { headers })
      );
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Maneja errores de las peticiones HTTP
   */
  private handleError(error: any): any {
    // Normalizar y preservar la mayor cantidad de información posible del backend
    if (error instanceof HttpErrorResponse) {
      const normalized = {
        status: error.status,
        code: error.error?.code ?? error.statusText ?? 'HTTP_ERROR',
        message:
          error.status === 0
            ? 'Error de conexión. Verifica tu conexión a internet.'
            : (error.error?.message || error.message || `Error HTTP ${error.status}`),
        details: error.error?.details ?? error.error?.errors ?? null,
        errorType: error.error?.errorType ?? null, // Nuevo: tipo específico de error del backend
        newEmailExistingDocument: error.error?.newEmailExistingDocument ?? false, // Nuevo: flag para email nuevo con documento existente
        raw: error
      };
      return normalized;
    }

    // Error no-HTTP (cliente, parseo, etc.)
    return {
      status: undefined,
      code: error?.code ?? 'CLIENT_ERROR',
      message: error?.message || 'Error desconocido',
      details: error?.details ?? null,
      errorType: null,
      newEmailExistingDocument: false,
      raw: error
    };
  }

  /**
   * Busca un usuario por email o documento
   */

  async findUserByEmailOrDocument(email?: string, document?: string): Promise<any> {
    const params: any = {};
    if (email) params.email = email;
    if (document) params.document = document;
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    try {
      return await firstValueFrom(
        this.http.get<any>(`${this.API_BASE_URL}/auth/user`, { params, headers })
      );
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Inicia el flujo de olvido de contraseña (envía código al email)
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    try {
      return await firstValueFrom(
        this.http.post<{ message: string }>(
          `${this.API_BASE_URL}/auth/forgot-password`,
          { email }
        )
      );
    } catch (error) {
      throw this.handleError(error);
    }
  }
  /**
 * Completa el cambio de contraseña forzado (NEW_PASSWORD_REQUIRED)
 */
  async completeNewPassword(email: string, newPassword: string): Promise<{ message: string }> {
    try {
      // Recuperar session de localStorage
      const session = localStorage.getItem('cognitoSession');
      if (!session) {
        throw new Error('No hay session de Cognito disponible. Inicia sesión nuevamente.');
      }
      const result = await firstValueFrom(
        this.http.post<{ message: string }>(
          `${this.API_BASE_URL}/auth/complete-new-password`,
          { email, newPassword, session }
        )
      );
      // Limpiar session después de usarla
      localStorage.removeItem('cognitoSession');
      return result;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Confirma el cambio de contraseña usando el código recibido
   */
  async confirmForgotPassword(email: string, code: string, newPassword: string): Promise<{ message: string }> {
    try {
      return await firstValueFrom(
        this.http.post<{ message: string }>(
          `${this.API_BASE_URL}/auth/confirm-forgot-password`,
          { email, code, newPassword }
        )
      );
    } catch (error) {
      throw this.handleError(error);
    }
  }
}
